import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SearchX } from "lucide-react";
import { listings, STORE_URL } from "../../data/catalog";
import { ProductCard } from "./ProductCard";
import { Reveal, SectionLabel } from "./Reveal";

const GRADE_FILTERS = [
  { key: "all", label: "All" },
  { key: "g10", label: "Gem Mint 10", test: (l) => l.grade === 10 },
  { key: "g9", label: "Mint 9", test: (l) => l.grade === 9 },
  { key: "g87", label: "Grades 8–7", test: (l) => l.grade === 8 || l.grade === 7 },
  { key: "g63", label: "Grades 6–3", test: (l) => l.grade !== null && l.grade <= 6 },
  { key: "doujin", label: "Doujin ACG", test: (l) => l.grade === null },
];

export const Catalog = () => {
  const [filter, setFilter] = useState("all");
  const items = useMemo(() => {
    const f = GRADE_FILTERS.find((f) => f.key === filter);
    return f && f.test ? listings.filter(f.test) : listings;
  }, [filter]);

  return (
    <section id="shop" className="relative bg-[#0B0D13] py-24 lg:py-36" data-testid="showcase-section">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <Reveal className="mb-12 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <div>
            <SectionLabel n="02">The vault</SectionLabel>
            <h2 className="font-display text-4xl font-black uppercase leading-[0.9] sm:text-6xl">Live on eBay<br /><span className="text-outline">right now.</span></h2>
          </div>
          <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost group self-start lg:self-auto" data-testid="showcase-store-btn">
            Browse the full store
          </a>
        </Reveal>
        <Reveal delay={0.05} className="sticky top-16 z-30 -mx-5 bg-[#0B0D13]/90 px-5 py-3 backdrop-blur-xl lg:-mx-2 lg:px-2" data-testid="catalog-filters">
          <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {GRADE_FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} className="relative shrink-0 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold transition-colors duration-300" data-testid={`filter-${f.key}`}>
                {filter === f.key && <motion.span layoutId="filter-pill" className="absolute inset-0 rounded-full bg-volt" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
                <span className={`relative whitespace-nowrap ${filter === f.key ? "text-black" : "text-slate-200"}`}>{f.label}</span>
              </button>
            ))}
          </div>
        </Reveal>
        <motion.div layout className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="showcase-grid">
          <AnimatePresence>
            {items.map((l) => <ProductCard key={l.id} l={l} />)}
          </AnimatePresence>
        </motion.div>
        {items.length === 0 && (
          <div className="py-20 text-center text-slate-400" data-testid="catalog-empty"><SearchX className="mx-auto h-10 w-10 text-slate-600" /></div>
        )}
      </div>
    </section>
  );
};
